import { ApiResponse, User } from "../types/auth";
import { request, requestResult } from "@/lib/apiClient";
import { getApiBaseUrl } from "@/lib/apiBaseUrl";
import { queryClient } from "@/lib/queryClient";
import { strategiesFromUser } from "@/lib/userStrategies";

export const userStrategiesKey = (uid?: string) =>
  ["auth", "strategies", uid] as const;

/**
 * Strategy reads are cached by TanStack Query (see useUserStrategies). This just
 * marks the cache stale so the next read refetches.
 */
export function invalidateUserStrategiesCache(firebaseUid?: string): void {
  queryClient.invalidateQueries({
    queryKey: firebaseUid
      ? userStrategiesKey(firebaseUid)
      : ["auth", "strategies"],
  });
}

/** Push the latest enrolled strategies into the shared query cache (instant UI). */
export function setUserStrategiesCache(
  firebaseUid: string,
  strategies: string[]
): void {
  queryClient.setQueryData(userStrategiesKey(firebaseUid), strategies);
}

/** After sync-user returns an updated user row, refresh auth + strategies caches. */
export function syncUserStrategiesFromProfile(
  firebaseUid: string,
  user: User | BackendUser
): string[] {
  const strategies = strategiesFromUser(user);
  setUserStrategiesCache(firebaseUid, strategies);
  return strategies;
}

interface UserSyncData {
  firebase_uid: string;
  email: string;
  name: string;
  phone?: string;
  avatar?: string;
  dob?: string;
  panCard?: string;
  aadharCard?: string;
  auth_method: "google" | "phone";
  selected_strategy?: string;
  broker?: string;
}

export interface OnboardingStatusPayload {
  success: boolean;
  firebase_uid: string;
  user_found: boolean;
  profile_complete: boolean;
  has_strategy_subscription: boolean;
  has_broker_credentials: boolean;
  strategies: string[];
  connection_count?: number;
}

export interface FyersUserPayload {
  client_id: string;
  name?: string;
  phone?: number;
  status?: string;
  [key: string]: unknown;
}

/** sync-user result, plus the bundled bootstrap data the backend now returns. */
export interface SyncUserResult extends ApiResponse<User> {
  onboarding?: OnboardingStatusPayload | null;
  fyersUser?: FyersUserPayload | null;
  connections?: Record<string, any>[] | null;
  allowedBrokers?: Record<string, any>[] | null;
}

interface BackendUser {
  id: string;
  firebase_uid: string;
  email: string;
  name: string;
  phone?: string;
  avatar?: string;
  is_verified?: boolean;
  dob?: string;
  pan_card?: string;
  aadhar_card?: string;
  selected_strategy?: string;
  Strategy_Optimus?: string | null;
  Strategy_Polaris_Lite?: string | null;
  Strategy_Pledge_Plus?: string | null;
  created_at: string;
  updated_at?: string;
}

function mapBackendUser(raw: BackendUser & { _raw_email?: string, _raw_phone?: string, _raw_pan?: string }): User {
  const selectedStrategies = strategiesFromUser(raw);
  
  let decodedEmail = raw.email;
  let decodedPhone = raw.phone;
  let decodedPan = raw.pan_card;

  try {
    if (raw._raw_email) decodedEmail = atob(raw._raw_email);
    if (raw._raw_phone) decodedPhone = atob(raw._raw_phone);
    if (raw._raw_pan) decodedPan = atob(raw._raw_pan);
  } catch (e) {
    console.error("Failed to decode raw user data", e);
  }

  return {
    id: raw.firebase_uid,
    firebase_uid: raw.firebase_uid,
    email: decodedEmail,
    name: raw.name || "",
    phone: decodedPhone,
    avatar: raw.avatar,
    isVerified: raw.is_verified || false,
    dob: raw.dob,
    panCard: decodedPan,
    aadharCard: raw.aadhar_card,
    selectedStrategy: raw.selected_strategy,
    selectedStrategies,
    broker: (raw as any).broker,
    createdAt: new Date(raw.created_at),
    updatedAt: new Date(raw.updated_at || raw.created_at),
  };
}

export interface ProfileCompletenessItem {
  key: "pan_card" | "phone" | "dob" | "legality";
  label: string;
  type: "text" | "tel" | "date" | "esign";
  present: boolean;
  value: string | null;
  action?: string;
}

export interface ProfileCompletenessResponse {
  success: boolean;
  firebase_uid: string;
  user_found: boolean;
  client_id?: string | null;
  profile_complete: boolean;
  missing_fields: string[];
  items: ProfileCompletenessItem[];
  message?: string;
}

export interface ProfileDetailsUpdate {
  firebase_uid: string;
  client_id?: string;
  pan_card?: string;
  phone?: string;
  dob?: string;
}

const authService = {
  getProfileCompleteness: (
    firebaseUid: string
  ): Promise<ProfileCompletenessResponse> =>
    request<ProfileCompletenessResponse>(
      `/auth/profile-completeness/${firebaseUid}`
    ),

  updateProfileDetails: (
    payload: ProfileDetailsUpdate
  ): Promise<ProfileCompletenessResponse> =>
    request<ProfileCompletenessResponse>(`/auth/profile-details`, {
      method: "POST",
      body: payload,
    }),

  syncUserData: async (userData: UserSyncData): Promise<SyncUserResult> => {
    const result = await requestResult<{
      success: boolean;
      user: BackendUser;
      message?: string;
      onboarding?: OnboardingStatusPayload | null;
      fyers_user?: FyersUserPayload | null;
      connections?: Record<string, any>[] | null;
      allowed_brokers?: Record<string, any>[] | null;
    }>("/auth/sync-user", { method: "POST", body: userData });

    if (!result.success || !result.data?.success || !result.data.user) {
      return { success: false, error: result.error || "Failed to sync user data" };
    }
    return {
      success: true,
      data: mapBackendUser(result.data.user),
      message: result.data.message,
      onboarding: result.data.onboarding ?? null,
      fyersUser: result.data.fyers_user ?? null,
      connections: result.data.connections ?? null,
      allowedBrokers: result.data.allowed_brokers ?? null,
    };
  },

  fetchBootstrapData: async (firebaseUid: string): Promise<SyncUserResult> => {
    const result = await requestResult<{
      success: boolean;
      user: BackendUser;
      message?: string;
      onboarding?: OnboardingStatusPayload | null;
      fyers_user?: FyersUserPayload | null;
      connections?: Record<string, any>[] | null;
      allowed_brokers?: Record<string, any>[] | null;
    }>(`/auth/bootstrap/${firebaseUid}`);

    if (!result.success || !result.data?.success || !result.data.user) {
      return { success: false, error: result.error || "Failed to fetch bootstrap data" };
    }
    return {
      success: true,
      data: mapBackendUser(result.data.user),
      message: result.data.message,
      onboarding: result.data.onboarding ?? null,
      fyersUser: result.data.fyers_user ?? null,
      connections: result.data.connections ?? null,
      allowedBrokers: result.data.allowed_brokers ?? null,
    };
  },

  getOnboardingStatus: async (firebaseUid: string) => {
    return request<{
      success: boolean;
      firebase_uid: string;
      user_found: boolean;
      profile_complete: boolean;
      has_strategy_subscription: boolean;
      has_broker_credentials: boolean;
      strategies: string[];
    }>(`/auth/onboarding-status/${firebaseUid}`);
  },

  getUserData: async (firebaseUid: string): Promise<ApiResponse<User>> => {
    try {
      const response = await request<{
        success: boolean;
        user: BackendUser;
      }>(`/auth/user/${firebaseUid}`);

      if (response.success && response.user) {
        return {
          success: true,
          data: mapBackendUser(response.user),
        };
      }
      return { success: false, error: "User not found" };
    } catch (error) {
      const message =
        error instanceof Error ? error.message : "Failed to get user data";
      if (message.includes("404") || message.includes("not found")) {
        return { success: false, error: "User not found" };
      }
      return { success: false, error: message };
    }
  },

  updateUserData: async (
    firebaseUid: string,
    updateData: Partial<UserSyncData>
  ): Promise<ApiResponse<User>> => {
    const result = await requestResult<{
      success: boolean;
      user: BackendUser;
      message?: string;
    }>(`/auth/user/${firebaseUid}`, {
      method: "PATCH",
      body: updateData,
    });

    if (!result.success || !result.data?.success || !result.data.user) {
      return { success: false, error: result.error || "Failed to update user" };
    }
    return {
      success: true,
      data: mapBackendUser(result.data.user),
      message: result.data.message,
    };
  },

  completeRegistration: async (userData: UserSyncData): Promise<ApiResponse<User>> => {
    return authService.syncUserData(userData);
  },

  verifyPan: async (pan: string): Promise<ApiResponse<unknown>> => {
    const result = await requestResult<{ success: boolean; message?: string }>(
      `/kra/verify-pan/${pan}`
    );
    if (!result.success) {
      return { success: false, error: result.error };
    }
    return {
      success: !!result.data?.success,
      data: result.data,
      message: result.data?.message,
    };
  },

  checkUserExists: async (firebaseUid: string): Promise<boolean> => {
    const response = await authService.getUserData(firebaseUid);
    return response.success;
  },

  getUserStrategies: async (
    firebaseUid: string,
    options?: { force?: boolean; hintUser?: User | BackendUser | null }
  ): Promise<string[]> => {
    if (!firebaseUid) return [];

    // When the user object already carries strategies, use them without a call
    // (TanStack Query also seeds this via initialData in useUserStrategies).
    if (!options?.force) {
      const fromUser = strategiesFromUser(options?.hintUser ?? null);
      if (fromUser.length) {
        return fromUser;
      }
    }

    try {
      const response = await request<Record<string, unknown>>(
        `/auth/strategies/${firebaseUid}`
      );
      let parsed: string[] = [];
      if (Array.isArray(response)) parsed = response as string[];
      else if (Array.isArray(response.selected_strategies)) {
        parsed = response.selected_strategies as string[];
      } else {
        const data = response.data as Record<string, unknown> | undefined;
        if (data && Array.isArray(data.selected_strategies)) {
          parsed = data.selected_strategies as string[];
        } else if (Array.isArray(response.strategies)) {
          parsed = response.strategies as string[];
        } else if (Array.isArray(response.data)) {
          parsed = response.data as string[];
        } else if (typeof response.selected_strategy === "string") {
          parsed = response.selected_strategy
            .split(",")
            .map((s) => s.trim())
            .filter(Boolean);
        }
      }
      return parsed;
    } catch {
      return [];
    }
  },

  healthCheck: async (): Promise<boolean> => {
    try {
      const response = await request<{ status?: string }>("/health");
      return response.status === "healthy";
    } catch {
      return false;
    }
  },

  getCurrentApiUrl: (): string => getApiBaseUrl(),

  refreshApiUrl: () => {
    /* no-op: base URL is resolved per request */
  },

  sendEmailOtp: async (email: string): Promise<boolean> => {
    try {
      const response = await request<{ success: boolean; message: string }>("/auth/send-email-otp", {
        method: "POST",
        body: { email },
      });
      return response.success;
    } catch (error) {
      console.error("Error sending email OTP:", error);
      throw error;
    }
  },

  verifyEmailOtp: async (email: string, otp: string): Promise<{ success: boolean; message?: string }> => {
    try {
      const response = await request<{ success: boolean; token: string; firebase_uid: string; message: string }>("/auth/verify-email-otp", {
        method: "POST",
        body: { email, otp },
      });
      return { success: response.success, message: response.message };
    } catch (error: any) {
      console.error("Error verifying email OTP:", error);
      return { success: false, message: error.message || "Invalid OTP code" };
    }
  },
};

export default authService;
